import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/Button';
import { Wordmark } from './Wordmark';
import { useConfirm } from './ConfirmProvider';

interface HeaderProps {
  /** Called when the mobile hamburger is tapped. */
  onOpenDrawer?: () => void;
  /** Called when the keyboard-shortcuts ? button is pressed. */
  onOpenHelp?: () => void;
}

function roleWorkbenchLabel(role: string | undefined): string {
  switch (role) {
    case 'admin':
      return 'Admin Workbench';
    case 'lead':
      return 'Lead Workbench';
    case 'senior_assessor':
      return 'Senior Assessor Workbench';
    case 'assessor':
      return 'Assessor Workbench';
    case 'item_bank_manager':
      return 'Item Bank Workbench';
    default:
      return 'Prism Workbench';
  }
}

export function Header({ onOpenDrawer, onOpenHelp }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    // Match the portal: signing out kills the session and forces a fresh
    // password + OTP next visit. Accidental clicks happen — confirm first.
    const ok = await confirm({
      title: 'Sign Out of Prism?',
      message:
        "You'll need your password and a fresh OTP next time you sign in.",
      confirmLabel: 'Sign out',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/dashboard/login', { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  return (
    <header className="flex h-[64px] items-center justify-between border-b border-[var(--color-rule)] bg-[var(--color-surface)] px-4 sm:px-8 lg:h-[68px] lg:px-12">
      <div className="flex items-center gap-3 sm:gap-5">
        {/* Mobile hamburger — opens the sidebar drawer */}
        {onOpenDrawer && (
          <button
            type="button"
            onClick={onOpenDrawer}
            aria-label="Open navigation menu"
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-[4px] text-[var(--color-ink)] hover:bg-[var(--color-surface-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)] md:hidden"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        )}
        <Link
          to="/dashboard/"
          aria-label="Prism — back to dashboard home"
          className="rounded-[3px] outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-primary)]"
        >
          <Wordmark size={22} />
        </Link>
        <span className="hidden h-6 w-px bg-[var(--color-rule-strong)] md:block" aria-hidden="true" />
        <span className="hidden font-sans text-[12px] font-semibold uppercase tracking-[0.16em] text-[var(--color-ink-soft)] md:inline">
          {roleWorkbenchLabel(user?.role)}
        </span>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {/* Keyboard shortcuts help (desktop only — mobile users don't have a kb) */}
        {onOpenHelp && (
          <button
            type="button"
            onClick={onOpenHelp}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
            className="hidden h-9 w-9 cursor-pointer items-center justify-center rounded-[4px] border border-[var(--color-rule-strong)] font-mono text-[13px] font-semibold text-[var(--color-ink-muted)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)] md:flex"
          >
            ?
          </button>
        )}

        {user && (
          <Link
            to="/dashboard/account"
            title="Your account — profile, password and devices"
            aria-label="Your account"
            className="flex items-center gap-2.5 rounded-full border border-[var(--color-rule-strong)] py-1 pl-1 pr-1.5 transition-colors hover:border-[var(--color-primary)] hover:bg-[var(--color-surface-muted)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-primary)] md:pr-3"
          >
            <span
              aria-hidden="true"
              className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[var(--color-primary)] font-sans text-[13px] font-semibold uppercase leading-none text-white"
            >
              {(user.email.trim()[0] ?? '?').toUpperCase()}
            </span>
            <span className="hidden text-left leading-tight md:block">
              <span className="block max-w-[200px] truncate font-sans text-[13px] font-medium text-[var(--color-ink)]">
                {user.email}
              </span>
              <span className="block font-sans text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--color-ink-soft)]">
                {user.role.replace(/_/g, ' ')}
              </span>
            </span>
          </Link>
        )}
        <Button
          variant="secondary"
          size="sm"
          onClick={handleLogout}
          isLoading={isLoggingOut}
          aria-label="Sign out of Prism"
        >
          Sign out
        </Button>
      </div>
    </header>
  );
}
